import { Avatar } from './Avatar';
import { formatTime } from '../../lib/format';
import { previewDaConversa, type ConversaSalva } from '../../lib/conversas';

function formatDia(epochMs: number): string {
  const d = new Date(epochMs);
  const hoje = new Date();
  const ontem = new Date(hoje);
  ontem.setDate(hoje.getDate() - 1);
  if (d.toDateString() === hoje.toDateString()) return formatTime(epochMs);
  if (d.toDateString() === ontem.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/**
 * "Home" no estilo do WhatsApp atual: título grande "Conversas", busca em pill,
 * lista de conversas anteriores com a farmácia, e o botão verde de NOVA conversa
 * (FAB, canto inferior direito) — o jeito canônico de recomeçar do zero.
 */
export function HomeScreen({
  conversas,
  onAbrir,
  onNova,
}: {
  conversas: ConversaSalva[];
  onAbrir: (c: ConversaSalva) => void;
  onNova: () => void;
}) {
  return (
    <div className="w-full h-[100dvh] overflow-hidden flex flex-col bg-[#F7F3EC] relative">
      {/* header grande do design novo */}
      <div className="flex-shrink-0 px-4 pb-2" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 10px)' }}>
        <div className="flex items-center justify-between h-10">
          <h1 className="text-[28px] font-bold text-[#0A0A0A] tracking-[-0.02em]">Conversas</h1>
          <Avatar size={34} />
        </div>
        {/* busca (visual) */}
        <div className="mt-2 flex items-center gap-2 bg-white rounded-full border border-black/[0.05] shadow-[0_1px_2px_rgba(0,0,0,0.04)] px-3.5 h-[38px] text-[#9A9A9E]">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="M21 21l-4.35-4.35" />
          </svg>
          <span className="text-[15px]">Buscar</span>
        </div>
      </div>

      {/* lista */}
      <div className="wa-scroll flex-1 overflow-y-auto">
        {conversas.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-10 text-center -mt-8">
            <Avatar size={72} />
            <p className="text-[15px] text-[#6B6B70] leading-snug">
              Nenhuma conversa ainda.
              <br />
              Toque no botão verde pra pedir seu primeiro orçamento. 💊
            </p>
          </div>
        ) : (
          <ul>
            {conversas.map((c) => {
              const ultima = c.messages.at(-1);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => onAbrir(c)}
                    className="w-full flex items-center gap-3 px-4 py-[10px] text-left active:bg-black/[0.04] transition-colors"
                  >
                    <Avatar size={52} />
                    <div className="flex-1 min-w-0 border-b border-black/[0.05] pb-[10px] -mb-[10px]">
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-[16.5px] font-semibold text-[#0A0A0A] truncate">
                          Minas Brasil
                        </span>
                        <span className="text-[12.5px] text-[#8A8A8E] flex-shrink-0">
                          {formatDia(ultima?.timestamp ?? c.updatedAt)}
                        </span>
                      </div>
                      <div className="text-[14.5px] text-[#6B6B70] truncate leading-snug">
                        {previewDaConversa(c)}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* FAB verde: NOVA conversa */}
      <button
        type="button"
        aria-label="Nova conversa"
        onClick={onNova}
        className="absolute right-4 w-[54px] h-[54px] rounded-[18px] bg-[#1DAB61] text-white flex items-center justify-center shadow-[0_4px_14px_-2px_rgba(29,171,97,0.55)] active:scale-95 transition-transform"
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 18px)' }}
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.005 3.175H4.674C3.642 3.175 3 3.789 3 4.821V21.02l3.544-3.514h12.461c1.033 0 2.064-1.06 2.064-2.093V4.821c-.001-1.032-1.032-1.646-2.064-1.646zm-4.989 9.869H7.041V11.1h6.975v1.944zm3-4H7.041V7.1h9.975v1.944z" />
        </svg>
      </button>
    </div>
  );
}
